import { MedplumClient } from "@medplum/core";
import { Questionnaire } from "@medplum/fhirtypes";

import { getOrCreateCombinedProcedureCodesValueSet } from "@/settings/components/FeeTable/services/combined-procedure-codes";
import { getCombinedProcedureCodesValueSetUrl } from "@/utils/billing";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { fetchCptCodeQuestionnaire } from "./questionnaire";

/**
 * Updates the CPT code answerValueSet in a questionnaire
 */
export function updateCptCodeAnswerValueSet(
  questionnaire: Questionnaire,
  valueSetUrl: string,
): Questionnaire {
  return {
    ...questionnaire,
    item: questionnaire.item?.map((group) => {
      if (group.linkId !== "cpt") return group;
      return {
        ...group,
        item: group.item?.map((item) =>
          item.linkId === "cptCode" ? { ...item, answerValueSet: valueSetUrl } : item,
        ),
      };
    }),
  };
}

/**
 * Loads the CPT questionnaire bound to the practice's combined procedure codes ValueSet.
 *
 * When the rebuild fails the questionnaire still searches that URL — the same contract as the
 * Procedures picker — so the drawer keeps offering the practice's fee and custom codes.
 */
export async function loadCptQuestionnaireWithCombinedValueSet(
  medplum: MedplumClient,
  practiceId?: string,
): Promise<Questionnaire | undefined> {
  const questionnaire = await fetchCptCodeQuestionnaire(medplum);
  if (!questionnaire || !practiceId) {
    return questionnaire;
  }

  const combinedUrl = getCombinedProcedureCodesValueSetUrl(practiceId);

  try {
    // upsertCustomProcedureCode / deleteCustomProcedureCode already refresh the combined ValueSet.
    const combinedValueSet = await getOrCreateCombinedProcedureCodesValueSet(medplum, practiceId);
    return updateCptCodeAnswerValueSet(questionnaire, combinedValueSet.url ?? combinedUrl);
  } catch (error) {
    captureExceptionWithoutPhi("Failed to build combined procedure codes ValueSet", error, {
      feature: "combined-procedure-codes",
      operation: "cpt-questionnaire-open",
      practiceId,
    });
    return updateCptCodeAnswerValueSet(questionnaire, combinedUrl);
  }
}
